/**
 * DomHud — binds the static DOM chrome (top bar, tabs, backup dialog) to
 * Playroom state and scene switches. DOM text is crisper than in-canvas
 * text, especially at non-integer DPRs, so all data readouts live here.
 */

import { onComputeChange, claimBackupReward, world } from '@/net/PlayroomBridge';

export function mountDomHud(): void {
  bindComputeReadout();
  bindSceneTabs();
  bindBackupDialog();
  bindVoidIndex();
}

function bindComputeReadout(): void {
  const el = document.querySelector<HTMLElement>('[data-bind="compute"]');
  if (!el) return;
  onComputeChange((n) => {
    el.textContent = `COMPUTE ${n.toString().padStart(6, '0')}`;
  });
}

function bindSceneTabs(): void {
  const sceneLabel = document.querySelector<HTMLElement>('[data-bind="scene"]');
  const buttons = document.querySelectorAll<HTMLButtonElement>('.tab[data-scene]');
  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      const target = btn.dataset.scene;
      if (!target) return;
      // Drive the Phaser game via a global hook — set by the game instance.
      const game = window.__phaserGame;
      if (!game) return;
      const current =
        game.scene.getScenes(true).find((s) =>
          ['CommandHubScene', 'BattleFrontScene'].includes(s.scene.key),
        )?.scene.key ?? 'CommandHubScene';
      if (current === target) return;
      game.scene.stop(current);
      game.scene.start(target);
      buttons.forEach((b) => b.setAttribute('aria-current', String(b === btn)));
      if (sceneLabel) {
        sceneLabel.textContent =
          target === 'BattleFrontScene' ? 'BATTLE FRONT' : 'COMMAND HUB';
      }
    });
  });
}

function bindBackupDialog(): void {
  const dialog = document.getElementById('backup-dialog') as HTMLDialogElement | null;
  const open = document.getElementById('btn-backup');
  const link = document.getElementById('btn-link');
  if (!dialog || !open) return;

  open.addEventListener('click', () => {
    if (typeof dialog.showModal === 'function') dialog.showModal();
  });

  link?.addEventListener('click', async (e) => {
    e.preventDefault();
    try {
      const res = await claimBackupReward();
      if (res.awarded) {
        flashToast(`+500 COMPUTE // BACKUP ESTABLISHED`);
      } else {
        flashToast(`LINK REQUIRED — SIGN IN VIA PLAYROOM LOBBY`);
      }
    } catch (err) {
      console.error('[CandleX] backup reward failed', err);
      flashToast('BACKUP LINK FAILED');
    } finally {
      dialog.close();
    }
  });
}

function bindVoidIndex(): void {
  const voidEl = document.querySelector<HTMLElement>('[data-bind="void"]');
  const sigEl = document.querySelector<HTMLElement>('[data-bind="signal"]');
  const pwr = document.querySelector<HTMLElement>('[data-bind="pwr"]');
  const flux = document.querySelector<HTMLElement>('[data-bind="flux"]');
  setInterval(() => {
    const v = world.getVoidIndex();
    if (voidEl) voidEl.textContent = `V ${v.toFixed(2)}`;
    if (sigEl) sigEl.textContent = `SIG ${(1 - v).toFixed(2)}`;
    if (pwr) pwr.textContent = Math.round(4000 + Math.sin(Date.now() / 1000) * 300).toString();
    if (flux) flux.textContent = (0.5 + Math.sin(Date.now() / 700) * 0.2).toFixed(3);
  }, 500);
}

function flashToast(msg: string): void {
  const el = document.createElement('div');
  el.textContent = msg;
  Object.assign(el.style, {
    position: 'fixed',
    top: '16px',
    left: '50%',
    transform: 'translateX(-50%)',
    padding: '10px 16px',
    background: '#0a0a0a',
    border: '1px solid #1d6bff',
    color: '#ffffff',
    fontFamily: 'IBM Plex Mono, monospace',
    fontSize: '12px',
    letterSpacing: '0.12em',
    zIndex: '9999',
    boxShadow: '0 0 20px rgba(29,107,255,0.4)',
  } satisfies Partial<CSSStyleDeclaration>);
  document.body.appendChild(el);
  setTimeout(() => el.remove(), 2400);
}

// Expose the Phaser game to the DOM layer so the tabs can drive scenes.
// Phaser doesn't provide a built-in global handle, so we attach one from main.ts.
declare global {
  interface Window {
    __phaserGame?: import('phaser').Game;
  }
}
